/**
 * The Tasks screen's one tab bar on a phone: `Table · Now · Today ·
 * Schedules`.
 *
 * It replaces two controls that used to stack on a 390px screen -- the
 * laptop's Table / Calendar switch and, inside the calendar, the phone's
 * own Now / Today / Schedules switch -- so the content starts ~a fifth of
 * the screen higher (phone header option C, 2026-10-30). Table is the
 * table view; the other three are the phone calendar on that tab. Where
 * each one lives in the URL is the screen's business (`TasksScreen`,
 * `calendar-url.ts`, `phone-url.ts`); this only draws the choice.
 *
 * A real tablist (WAI-ARIA tabs pattern): one tab in the Tab order,
 * arrow keys / Home / End move between them and choose as they go, and
 * every tab is at least 44px tall.
 */
import { useEffect, useRef, type KeyboardEvent } from 'react';
import {
  TASKS_PHONE_TABS,
  tasksPhonePanelId,
  tasksPhoneTabId,
  type TasksPhoneTab,
} from './tasks-phone-tab';
import './TasksPhoneTabs.css';

/**
 * The tab just chosen, for the bar that draws it to take focus.
 *
 * The bar is drawn in two places -- over the table, and in the phone
 * calendar's sticky head -- so a move between the table and a calendar
 * tab unmounts this bar and mounts another: the button that was pressed
 * is gone and focus would fall to the page, leaving a keyboard or
 * switch user at the top of the document. The new bar picks it up here,
 * and only when focus really was lost.
 */
let focusAfterMove: TasksPhoneTab | null = null;

export function TasksPhoneTabs({
  value,
  onChange,
}: {
  value: TasksPhoneTab;
  onChange: (tab: TasksPhoneTab) => void;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    const want = focusAfterMove;
    focusAfterMove = null;
    if (want !== value) return;
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) return;
    refs.current[TASKS_PHONE_TABS.findIndex((t) => t.id === value)]?.focus();
  }, [value]);

  const choose = (tab: TasksPhoneTab) => {
    focusAfterMove = tab;
    onChange(tab);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = TASKS_PHONE_TABS.length - 1;
    const next =
      e.key === 'ArrowRight'
        ? index === last
          ? 0
          : index + 1
        : e.key === 'ArrowLeft'
          ? index === 0
            ? last
            : index - 1
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? last
              : null;
    if (next === null) return;
    e.preventDefault();
    refs.current[next]?.focus();
    choose(TASKS_PHONE_TABS[next]!.id);
  };

  return (
    <div className="nx-tasks-tabs" role="tablist" aria-label="Tasks view" data-testid="tasks-tabs">
      {TASKS_PHONE_TABS.map((t, i) => {
        const on = t.id === value;
        return (
          <button
            key={t.id}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={tasksPhoneTabId(t.id)}
            aria-selected={on}
            // Only the shown panel exists, so only its tab names one.
            aria-controls={on ? tasksPhonePanelId(t.id) : undefined}
            tabIndex={on ? 0 : -1}
            className={`nx-tasks-tabs__tab${on ? ' is-on' : ''}`}
            onClick={() => {
              if (!on) choose(t.id);
            }}
            onKeyDown={(e) => onKeyDown(e, i)}
            data-testid={`tasks-tab-${t.id}`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
